import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { apiReloginSaga } from "modules/auth/sagas";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLeading } from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { projectsActions } from "./slices";
import {
  AssignGroupToProjectParams,
  CreateCLIUserParams,
  CreateCLIUserResponse,
  CreateProjectParams,
  CreateProjectResponse,
  DeleteCLIUserParams,
  DeleteCLIUserResponse,
  DeleteProjectParams,
  GetCLIUserParams,
  GetCLIUserResponse,
  GetCLIUsersParams,
  GetCLIUsersResponse,
  GetOrganizationProjectsParams,
  GetOrganizationProjectsResponse,
  GetProjectParams,
  GetProjectResponse,
  RemoveGroupFromProjectParams,
  UpdateCLIUserParams,
  UpdateCLIUserResponse,
  UpdateProjectParams,
  UpdateProjectResponse
} from "./types";

export function* getProjectSaga(
  action: PayloadAction<GetProjectParams>
): SagaIterator<void> {
  try {
    const { regionId, id } = action.payload;
    const response: AxiosResponse<GetProjectResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-identity/method/projects/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(projectsActions.getProjectSuccess(response.data));
    }
  } catch (e) {
    yield put(
      projectsActions.getProjectFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getOrganizationProjectsSaga(
  action: PayloadAction<GetOrganizationProjectsParams>
): SagaIterator<void> {
  try {
    const response: AxiosResponse<GetOrganizationProjectsResponse> = yield call(
      axiosInstance.get,
      `gotham-enterprises/method/orgs/${action.payload.organizationId}/projects`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(projectsActions.getOrganizationProjectsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      projectsActions.getOrganizationProjectsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get organization projects data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createProjectSaga(
  action: PayloadAction<CreateProjectParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the project...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, organizationId, data } = action.payload;
    const response: AxiosResponse<CreateProjectResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-identity/method/${organizationId}/projects/${data.name}`
    );

    // Start new session to gain access to the created project
    yield* apiReloginSaga();
    yield put(projectsActions.createProjectSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Project has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.createProjectFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create project",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateProjectSaga(
  action: PayloadAction<UpdateProjectParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the project...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, organizationId, id, data } = action.payload;
    const response: AxiosResponse<UpdateProjectResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-identity/method/${organizationId}/projects/${id}`,
      data
    );
    yield put(projectsActions.updateProjectSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Project has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.updateProjectFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update project",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteProjectSaga(
  action: PayloadAction<DeleteProjectParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the project...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, organizationId, id } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-identity/method/${organizationId}/projects/${id}`
    );
    yield put(projectsActions.deleteProjectSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Project has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.deleteProjectFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete project",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getCLIUserSaga(
  action: PayloadAction<GetCLIUserParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetCLIUserResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-identity/method/${projectId}/cli-users/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(projectsActions.getCLIUserSuccess(response.data));
    }
  } catch (e) {
    yield put(
      projectsActions.getCLIUserFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get CLI user data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getCLIUsersSaga(
  action: PayloadAction<GetCLIUsersParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetCLIUsersResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-identity/method/${projectId}/cli-users`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(projectsActions.getCLIUsersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      projectsActions.getCLIUsersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get CLI users data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createCLIUserSaga(
  action: PayloadAction<CreateCLIUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the CLI user...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateCLIUserResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-identity/method/${projectId}/cli-users`,
      data
    );
    yield put(projectsActions.createCLIUserSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "CLI user has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.createCLIUserFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create CLI user",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateCLIUserSaga(
  action: PayloadAction<UpdateCLIUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the CLI user...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateCLIUserResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-identity/method/${projectId}/cli-users/${id}`,
      data
    );
    yield put(projectsActions.updateCLIUserSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "CLI user has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.updateCLIUserFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update CLI user",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteCLIUserSaga(
  action: PayloadAction<DeleteCLIUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete CLI user...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteCLIUserResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-identity/method/${projectId}/cli-users/${id}`
    );
    yield put(projectsActions.deleteCLIUserSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete CLI user has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.deleteCLIUserFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete CLI user",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* assignGroupToProjectSaga(
  action: PayloadAction<AssignGroupToProjectParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Assigning group to the project...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, organizationId, groupId, projectId } = action.payload;
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-identity/method/${organizationId}/projects/${projectId}/groups/${groupId}`
    );
    yield put(projectsActions.assignGroupToProjectSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Group has been successfully assigned to the project.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.assignGroupToProjectFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to assign group to the project",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* removeGroupFromProjectSaga(
  action: PayloadAction<RemoveGroupFromProjectParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Removing group from the project...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, organizationId, groupId, projectId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-identity/method/${organizationId}/projects/${projectId}/groups/${groupId}`
    );
    yield put(projectsActions.removeGroupFromProjectSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Group has been successfully removed from the project.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      projectsActions.removeGroupFromProjectFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to remove group from the project",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(projectsActions.getProjectRequest, getProjectSaga),
    takeLeading(
      projectsActions.getOrganizationProjectsRequest,
      getOrganizationProjectsSaga
    ),
    takeEvery(projectsActions.createProjectRequest, createProjectSaga),
    takeEvery(projectsActions.updateProjectRequest, updateProjectSaga),
    takeEvery(projectsActions.deleteProjectRequest, deleteProjectSaga),
    takeLeading(projectsActions.getCLIUserRequest, getCLIUserSaga),
    takeLeading(projectsActions.getCLIUsersRequest, getCLIUsersSaga),
    takeEvery(projectsActions.createCLIUserRequest, createCLIUserSaga),
    takeEvery(projectsActions.updateCLIUserRequest, updateCLIUserSaga),
    takeEvery(projectsActions.deleteCLIUserRequest, deleteCLIUserSaga),
    takeEvery(
      projectsActions.assignGroupToProjectRequest,
      assignGroupToProjectSaga
    ),
    takeEvery(
      projectsActions.removeGroupFromProjectRequest,
      removeGroupFromProjectSaga
    )
  ]);
}
